
import {of as observableOf,  Observable } from 'rxjs';

import {shareReplay,  distinctUntilChanged } from 'rxjs/operators';
import { Respondent } from './../models/respondent.model';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import {
  LocalizedProcess, ProcessParticipation, ProcessParticipationRespondent,
  ProcessParticipationAnswer, AppraiseeProcessDetail, ProcessDetail,
  ProcessActionNotificationType, ProcessRespondentSelectionCriterionType, Process
} from '../models/process-models';
import { FileToUpload } from '../models/file-to-upload.model';
import { Survey } from '../models/questions';
import { ProcessValidationHelper } from '../helpers/';
import { AddRespondentsByBURequest } from '../models/process-models/add-respondents-by-bu-request.model';

@Injectable()
export class ProcessParticipationService {

  private readonly apiController = 'api/processparticipation';

  constructor(private readonly httpService: HttpClient) { }

  getByProcessId(processId: number): Observable<Array<ProcessParticipation>> {
    return this.httpService.get(`${this.apiController}/${processId}`) as Observable<Array<ProcessParticipation>>;
  }

  getSurveyByProcessId(processId: number): Observable<Survey> {
      return this.httpService.get(`${this.apiController}/survey/${processId}`).pipe(shareReplay()) as Observable<Survey>;
  }

  updateByFile(fileUpload: FileToUpload): Observable<Array<ProcessParticipation>> {
    return this.httpService.post(`${this.apiController}/${fileUpload.id}/updateByFile`,
      this.getFileFormData(fileUpload)) as Observable<Array<ProcessParticipation>>;
  }

  updateNotCompletedByProcesses(file, selectedProcessses: Array<LocalizedProcess>): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    selectedProcessses.forEach(p => formData.append('durationIds', p.durationId.toString()));
    return this.httpService.post(`${this.apiController}/updateNotCompleted`, formData) as Observable<any>;
  }

  updateNotCompleted(fileUpload: FileToUpload): Observable<ProcessDetail> {
    return this.httpService.post(`${this.apiController}/${fileUpload.id}/updateNotCompleted`,
      this.getFileFormData(fileUpload)).pipe(shareReplay()) as Observable<ProcessDetail>;
  }

  exportOne(processId: number): Observable<any> {
    return this.httpService.get(`${this.apiController}/${processId}/download`, this.getRequestOptions()).pipe(distinctUntilChanged());
  }

  exportSelectedByProcessIds(httpParams: HttpParams): Observable<any> {
    return this.httpService.get(`${this.apiController}/download/selected`, this.getRequestOptions(httpParams)).pipe(distinctUntilChanged());
  }

  getAll(isSelfAssessment: boolean): Observable<Array<ProcessParticipationRespondent>> {
    return this.httpService.get(`${this.apiController}/surveys/${isSelfAssessment}`) as Observable<Array<ProcessParticipationRespondent>>;
  }

  add(processParticipation: ProcessParticipation, isNotificationNeeded = true): Observable<Array<Respondent>> {
    return this.httpService
        .post(`${this.apiController}/addRespondents/${isNotificationNeeded}`, processParticipation)
        .pipe(distinctUntilChanged()) as Observable<Array<Respondent>>;
  }

  addMultiple(processParticipations: Array<ProcessParticipation>, isNotificationNeeded = true): Observable<Array<Respondent>> {
    return this.httpService
        .post(`${this.apiController}/addRespondentsMultiple/${isNotificationNeeded}`, processParticipations)
        .pipe(distinctUntilChanged()) as Observable<Array<Respondent>>;
  }

  addByBU(request: AddRespondentsByBURequest, isNotificationNeeded = true): Observable<Array<Respondent>> {
    return this.httpService
        .post(`${this.apiController}/addRespondentsByBU/${isNotificationNeeded}`, request)
        .pipe(distinctUntilChanged()) as Observable<Array<Respondent>>;
  }

  addByBUMultiple(requests: Array<AddRespondentsByBURequest>, isNotificationNeeded = true): Observable<Array<Respondent>> {
    return this.httpService
        .post(`${this.apiController}/addRespondentsByBUMultiple/${isNotificationNeeded}`, requests)
        .pipe(distinctUntilChanged()) as Observable<Array<Respondent>>;
  }

  addRespondentsToApprove(processParticipation: ProcessParticipation): Observable<Array<Respondent>> {
    return this.httpService
        .post(`${this.apiController}/addRespondentsToApprove`, processParticipation)
        .pipe(distinctUntilChanged()) as Observable<Array<Respondent>>;
  }

  addBURespondentsToApprove(processParticipation: ProcessParticipation): Observable<Array<Respondent>> {
    return this.httpService
        .post(`${this.apiController}/addBURespondentsToApprove`, processParticipation)
        .pipe(distinctUntilChanged()) as Observable<Array<Respondent>>;
  }

  delete(processDurationAppraiseeId, respondentId: number) {
    return this.httpService.delete(`${this.apiController}/${processDurationAppraiseeId}/${respondentId}`).pipe(distinctUntilChanged());
  }

  deleteAppraisee(processDurationAppraiseeId) {
    return this.httpService.delete(`${this.apiController}/${processDurationAppraiseeId}`).pipe(distinctUntilChanged());
  }

  deleteFromApprove(processDurationAppraiseeId, respondentId: number, selectiomCriterionType: ProcessRespondentSelectionCriterionType) {
    return this.httpService
        .delete(`${this.apiController}/approval/${processDurationAppraiseeId}/${selectiomCriterionType}/${respondentId}`)
        .pipe(distinctUntilChanged());
  }

  addOrUpdateAnswer(processParticipationId: number, answer: ProcessParticipationAnswer): Observable<Array<ProcessParticipationAnswer>> {
    return this.httpService
        .post(`${this.apiController}/answers/${processParticipationId}`, answer)
        .pipe(distinctUntilChanged()) as Observable<Array<ProcessParticipationAnswer>>;
  }

  deleteAnswer(processParticipationId: number, answer: ProcessParticipationAnswer): Observable<Array<ProcessParticipationAnswer>> {
    return this.httpService
        .post(`${this.apiController}/answers/delete/${processParticipationId}`, answer)
        .pipe(distinctUntilChanged()) as Observable<Array<ProcessParticipationAnswer>>;
  }

  getSurveyAnswers(processParticipationId: number): Observable<Array<ProcessParticipationAnswer>> {
    return this.httpService.get(`${this.apiController}/answers/${processParticipationId}`).pipe(
      shareReplay()) as Observable<Array<ProcessParticipationAnswer>>;
  }

  getRespondents(process: Process): Observable<Array<ProcessParticipation>> {
    if (!ProcessValidationHelper.isProcessValid(process, null)) {
      return observableOf([]);
    }
    return this.httpService.get(`${this.apiController}/respondents/${process.id}`) as Observable<Array<ProcessParticipation>>;
  }

  getRespondentsByProcessDurationAppraisee(processDurationAppraiseeId: number): Observable<Array<Respondent>> {
    return this.httpService.get(`${this.apiController}/respondentsApproval/${processDurationAppraiseeId}`) as Observable<Array<Respondent>>;
  }

  cancel(processDurationId, participations: Array<ProcessParticipation>): Observable<ProcessDetail> {
      return this.httpService.put(`${this.apiController}/cancel/${processDurationId}`, this.mapSelectedParticipations(participations))
          .pipe(distinctUntilChanged()) as Observable<ProcessDetail>;
  }

  deleteRespondents(processDurationId, participations: Array<ProcessParticipation>): Observable<ProcessDetail> {
      return this.httpService.put(`${this.apiController}/delete/${processDurationId}`, this.mapSelectedParticipations(participations))
          .pipe(distinctUntilChanged()) as Observable<ProcessDetail> ;
  }

  exportSelected(processId: number, participations: Array<ProcessParticipation>): Observable<any> {
    return this.httpService.post(`${this.apiController}/${processId}/exportSelected`, participations, { responseType: 'blob' });
  }

  completeProcessParticipation(processParticipationId: number, processId: number) {
    return this.httpService.put(`${this.apiController}/completesurvey/${processParticipationId}`, processId).pipe(distinctUntilChanged());
  }

  notifyAll(type: ProcessActionNotificationType, processDurationId: number): Observable<any>  {
    return this.httpService.put(`${this.apiController}/notifyAll/${type}/`, processDurationId).pipe(distinctUntilChanged());
  }

  notifySelected(type: ProcessActionNotificationType, participations: Array<ProcessParticipation>): Observable<any>  {
    return this.httpService.put(`${this.apiController}/notifySelected/${type}`, this.mapSelectedParticipations(participations))
          .pipe(distinctUntilChanged());
  }

  skipSurvey(processParticipationId: number): Observable<any> {
    return this.httpService.put(`${this.apiController}/skipsurvey/${processParticipationId}`, null) as Observable<any>;
  }

  private mapSelectedParticipations(participations: Array<ProcessParticipation>) {
    return participations.map(pp => Object.assign({
      processDurationAppraiseeId: pp.processDurationAppraiseeId,
      respondentIds: pp.respondents.map(r => r.id),
      isAppraiseeSelected: pp.isAppraiseeSelected
    }));
  }

  private getFileFormData(fileUpload) {
    const formData = new FormData();
    formData.append('file', fileUpload.file);
    return formData;
  }

  private getRequestOptions(httpParams?: HttpParams): Object {
    const options = {
      headers: new HttpHeaders(
        {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }),
      responseType: 'blob',
      params: httpParams
    };
    return options;
  }
}